import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  searchNutritionInstructions,
  createNutritionInstruction,
  updateNutritionInstruction,
  deleteNutritionInstruction,
  getInstructionCategories,
  InstructionCategory,
  NutritionInstruction,
} from '../services/nutritionInstructionsService';
import './Pages.css';

export const NutritionInstructions: React.FC = () => {
  const { user } = useAuth();
  const { theme, isDark: isDarkMode } = useTheme();
  
  const [instructions, setInstructions] = useState<NutritionInstruction[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<InstructionCategory | ''>('');
  
  // Modal de criar/editar instrução
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState<NutritionInstruction | null>(null);
  const [instructionForm, setInstructionForm] = useState<Partial<NutritionInstruction>>({
    title: '',
    category: 'general',
    content: '',
    tags: [],
    isPublic: false,
  });
  const [tagInput, setTagInput] = useState('');
  
  // Modal de visualizar instrução
  const [viewingInstruction, setViewingInstruction] = useState<NutritionInstruction | null>(null);

  const loadInstructions = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const results = await searchNutritionInstructions(
        searchTerm || undefined,
        selectedCategory || undefined,
        user.uid,
        true
      );
      setInstructions(results);
    } catch (error) {
      console.error('Erro ao carregar instruções:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, searchTerm, selectedCategory]);

  useEffect(() => {
    if (user) {
      loadInstructions();
    }
  }, [user, loadInstructions]);

  const handleCreate = () => {
    setEditingInstruction(null);
    setInstructionForm({
      title: '',
      category: 'general',
      content: '',
      tags: [],
      isPublic: false,
    });
    setIsModalOpen(true);
  };

  const handleEdit = (instruction: NutritionInstruction) => {
    setEditingInstruction(instruction);
    setInstructionForm({
      title: instruction.title,
      category: instruction.category,
      content: instruction.content,
      tags: instruction.tags,
      isPublic: instruction.isPublic,
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!user?.uid || !instructionForm.title || !instructionForm.content) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      if (editingInstruction) {
        await updateNutritionInstruction(
          user.uid,
          editingInstruction.id,
          instructionForm as Partial<NutritionInstruction>
        );
      } else {
        await createNutritionInstruction(
          user.uid,
          instructionForm as Omit<NutritionInstruction, 'id' | 'createdBy' | 'createdAt'>
        );
      }
      setIsModalOpen(false);
      await loadInstructions();
    } catch (error) {
      console.error('Erro ao salvar instrução:', error);
      alert('Erro ao salvar instrução. Tente novamente.');
    }
  };

  const handleDelete = async (instruction: NutritionInstruction) => {
    if (!user?.uid) return;
    if (!window.confirm(`Tem certeza que deseja deletar "${instruction.title}"?`)) return;

    try {
      await deleteNutritionInstruction(user.uid, instruction.id);
      await loadInstructions();
    } catch (error) {
      console.error('Erro ao deletar instrução:', error);
      alert('Erro ao deletar instrução. Tente novamente.');
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !instructionForm.tags?.includes(tagInput.trim())) {
      setInstructionForm({
        ...instructionForm,
        tags: [...(instructionForm.tags || []), tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setInstructionForm({
      ...instructionForm,
      tags: instructionForm.tags?.filter(t => t !== tag) || [],
    });
  };

  const categories = getInstructionCategories();

  const formatContent = (content: string) => {
    // Converter markdown básico para HTML
    let html = content
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^\* (.*$)/gim, '<li>$1</li>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');
    
    // Envolver listas
    html = html.replace(/(<li>.*<\/li>)/g, '<ul>$1</ul>');
    
    return { __html: `<p>${html}</p>` };
  };

  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Instruções Nutricionais</h2>
          <p>Biblioteca de orientações nutricionais para seus pacientes.</p>
        </div>
        <div className="ns-page-header-actions">
          <Button onClick={handleCreate}>+ Nova Instrução</Button>
        </div>
      </div>

      <Card title="Filtros e Busca">
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <label className="ns-field" style={{ flex: 1, minWidth: '200px' }}>
            <span>Buscar</span>
            <input
              type="text"
              className="ns-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Digite para buscar..."
            />
          </label>
          <label className="ns-field" style={{ minWidth: '200px' }}>
            <span>Categoria</span>
            <select
              className="ns-input"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as InstructionCategory | '')}
            >
              <option value="">Todas as categorias</option>
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </Card>

      {loading ? (
        <Card style={{ marginTop: '16px' }}>
          <p style={{ textAlign: 'center', color: theme.colors.textMuted }}>Carregando...</p>
        </Card>
      ) : instructions.length === 0 ? (
        <Card style={{ marginTop: '16px' }}>
          <p style={{ textAlign: 'center', color: theme.colors.textMuted, padding: '40px' }}>
            Nenhuma instrução encontrada. Crie uma nova instrução para começar.
          </p>
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px', marginTop: '16px' }}>
          {instructions.map((instruction) => (
            <Card
              key={instruction.id}
              title={instruction.title}
              style={{
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minHeight: '200px',
                display: 'flex',
                flexDirection: 'column',
              }}
              onClick={() => setViewingInstruction(instruction)}
            >
              <div style={{ marginBottom: '12px' }}>
                <span
                  style={{
                    fontSize: '11px',
                    padding: '4px 8px',
                    background: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                    color: isDarkMode ? '#93c5fd' : '#1e40af',
                    borderRadius: '4px',
                    fontWeight: 600,
                  }}
                >
                  {categories.find(c => c.value === instruction.category)?.label || instruction.category}
                </span>
                {instruction.isPublic && (
                  <span
                    style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      background: isDarkMode ? 'rgba(16, 185, 129, 0.2)' : '#d1fae5',
                      color: isDarkMode ? '#34d399' : '#065f46',
                      borderRadius: '4px',
                      fontWeight: 600,
                      marginLeft: '8px',
                    }}
                  >
                    Público
                  </span>
                )}
                {instruction.createdBy === 'system' && (
                  <span
                    style={{
                      fontSize: '11px',
                      padding: '4px 8px',
                      background: isDarkMode ? 'rgba(234, 179, 8, 0.2)' : '#fef3c7',
                      color: isDarkMode ? '#fbbf24' : '#92400e',
                      borderRadius: '4px',
                      fontWeight: 600,
                      marginLeft: '8px',
                    }}
                  >
                    Sistema
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: '13px',
                  color: theme.colors.textMuted,
                  marginBottom: '12px',
                  maxHeight: '100px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                dangerouslySetInnerHTML={formatContent(instruction.content.substring(0, 200) + '...')}
              />
              {instruction.tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '12px' }}>
                  {instruction.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        background: theme.colors.surface,
                        color: theme.colors.textMuted,
                        borderRadius: '4px',
                        border: `1px solid ${theme.colors.border}`,
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                  {instruction.tags.length > 3 && (
                    <span style={{ fontSize: '10px', color: theme.colors.textMuted }}>
                      +{instruction.tags.length - 3}
                    </span>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                <Button
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewingInstruction(instruction);
                  }}
                  style={{ fontSize: '11px', padding: '4px 8px', flex: 1 }}
                >
                  👁️ Ver
                </Button>
                {instruction.createdBy === user?.uid && (
                  <>
                    <Button
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(instruction);
                      }}
                      style={{ fontSize: '11px', padding: '4px 8px', flex: 1 }}
                    >
                      ✏️ Editar
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(instruction);
                      }}
                      style={{ fontSize: '11px', padding: '4px 8px', flex: 1 }}
                    >
                      🗑️ Deletar
                    </Button>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de Criar/Editar Instrução */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingInstruction ? 'Editar Instrução' : 'Nova Instrução'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <label className="ns-field">
            <span>Título *</span>
            <input
              type="text"
              className="ns-input"
              value={instructionForm.title || ''}
              onChange={(e) => setInstructionForm({ ...instructionForm, title: e.target.value })}
              placeholder="Ex: Alimentação Saudável - Guia Geral"
            />
          </label>

          <label className="ns-field">
            <span>Categoria *</span>
            <select
              className="ns-input"
              value={instructionForm.category || 'general'}
              onChange={(e) => setInstructionForm({ ...instructionForm, category: e.target.value as InstructionCategory })}
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </label>

          <label className="ns-field">
            <span>Conteúdo *</span>
            <textarea
              className="ns-textarea"
              rows={12}
              value={instructionForm.content || ''}
              onChange={(e) => setInstructionForm({ ...instructionForm, content: e.target.value })}
              placeholder="Digite o conteúdo da instrução. Você pode usar Markdown básico: # Título, ## Subtítulo, * Item de lista"
            />
            <p style={{ fontSize: '11px', color: theme.colors.textMuted, marginTop: '4px' }}>
              Dica: Use # para títulos, ## para subtítulos, * para listas
            </p>
          </label>

          <div>
            <label className="ns-field">
              <span>Tags</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  className="ns-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="Digite uma tag e pressione Enter"
                  style={{ flex: 1 }}
                />
                <Button variant="secondary" onClick={handleAddTag}>
                  Adicionar
                </Button>
              </div>
            </label>
            {instructionForm.tags && instructionForm.tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {instructionForm.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px',
                      padding: '4px 8px',
                      background: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : '#eff6ff',
                      color: isDarkMode ? '#93c5fd' : '#1e40af',
                      borderRadius: '4px',
                    }}
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: isDarkMode ? '#93c5fd' : '#1e40af',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: 0,
                        marginLeft: '4px',
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <label className="ns-field" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={instructionForm.isPublic || false}
              onChange={(e) => setInstructionForm({ ...instructionForm, isPublic: e.target.checked })}
            />
            <span>Tornar pública (outros nutricionistas podem ver e usar)</span>
          </label>
        </div>
      </Modal>

      {/* Modal de Visualizar Instrução */}
      <Modal
        isOpen={!!viewingInstruction}
        onClose={() => setViewingInstruction(null)}
        title={viewingInstruction?.title || ''}
        footer={
          <>
            {viewingInstruction && viewingInstruction.createdBy === user?.uid && (
              <Button
                variant="secondary"
                onClick={() => {
                  handleEdit(viewingInstruction);
                  setViewingInstruction(null);
                }}
              >
                Editar
              </Button>
            )}
            <Button variant="secondary" onClick={() => setViewingInstruction(null)}>
              Fechar
            </Button>
          </>
        }
      >
        {viewingInstruction && (
          <div>
            <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <span
                style={{
                  fontSize: '12px',
                  padding: '4px 8px',
                  background: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                  color: isDarkMode ? '#93c5fd' : '#1e40af',
                  borderRadius: '4px',
                  fontWeight: 600,
                }}
              >
                {categories.find(c => c.value === viewingInstruction.category)?.label || viewingInstruction.category}
              </span>
              {viewingInstruction.isPublic && (
                <span
                  style={{
                    fontSize: '12px',
                    padding: '4px 8px',
                    background: isDarkMode ? 'rgba(16, 185, 129, 0.2)' : '#d1fae5',
                    color: isDarkMode ? '#34d399' : '#065f46',
                    borderRadius: '4px',
                    fontWeight: 600,
                  }}
                >
                  Público
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: '14px',
                lineHeight: 1.6,
                color: theme.colors.text,
              }}
              dangerouslySetInnerHTML={formatContent(viewingInstruction.content)}
            />
            {viewingInstruction.tags.length > 0 && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: `1px solid ${theme.colors.border}` }}>
                <strong style={{ fontSize: '12px', color: theme.colors.textMuted, display: 'block', marginBottom: '8px' }}>Tags:</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {viewingInstruction.tags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        background: theme.colors.surface,
                        color: theme.colors.textMuted,
                        borderRadius: '4px',
                        border: `1px solid ${theme.colors.border}`,
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

